import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlusCircledIcon,
} from '@radix-ui/react-icons'

import { CreateToolModal } from '@/components/CreateToolModal'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/utils'

export type ToolSuggestionItem = {
  id: string
  name: string
}

type ToolSuggestionsSelectProps = {
  value?: string
  items: ToolSuggestionItem[]
  disabled?: boolean
  onSelect: (tool: ToolSuggestionItem) => void
}

export const ToolSuggestionsSelect = ({
  value = '',
  items,
  disabled = false,
  onSelect,
}: ToolSuggestionsSelectProps) => {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState(value)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [createModalInitialToolName, setCreateModalInitialToolName] =
    useState('')
  const suppressNextFocusOpenRef = useRef(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSearchValue(value)
  }, [value])

  const selectedToolId = useMemo(() => {
    const selectedItemById = items.find((item) => item.id === value)
    if (selectedItemById) {
      return selectedItemById.id
    }

    const selectedItemByName = items.find((item) => item.name === value)
    return selectedItemByName?.id || ''
  }, [items, value])

  const filteredTools = useMemo(() => {
    /**
     * Keep options deterministic and deduplicated by tool id.
     * Table-cell picker behavior should not change if upstream data repeats items.
     */
    const uniqueToolsById = new Map<string, ToolSuggestionItem>()
    items.forEach((item) => {
      if (!item?.id || !item?.name || uniqueToolsById.has(item.id)) {
        return
      }
      uniqueToolsById.set(item.id, item)
    })

    const normalizedSearch = searchValue.trim().toLowerCase()
    const uniqueTools = Array.from(uniqueToolsById.values())
    if (!normalizedSearch) {
      return uniqueTools
    }

    return uniqueTools.filter((item) =>
      item.name.toLowerCase().includes(normalizedSearch),
    )
  }, [items, searchValue])

  const onCloseOptions = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node
      const isInsideTrigger = containerRef.current?.contains(target)
      const isInsidePopover = popoverContentRef.current?.contains(target)

      if (!isInsideTrigger && !isInsidePopover) {
        setOpen(false)
        setSearchValue(value)
      }
    },
    [value],
  )

  const onEscapeOptions = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        setSearchValue(value)
      }
    },
    [value],
  )

  const onContextMenuOptions = useCallback(() => {
    setOpen(false)
    setSearchValue(value)
  }, [value])

  const ChevronIcon = useMemo(
    () => (open ? ChevronUpIcon : ChevronDownIcon),
    [open],
  )

  const openCreateToolModal = useCallback(() => {
    // Invariant: pre-fill from current search so create flow is one click.
    setCreateModalInitialToolName(searchValue.trim())
    setIsCreateModalOpen(true)
    setOpen(false)
  }, [searchValue])

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
      // Use capture so outside clicks are detected even when inner handlers
      // stop propagation (e.g. clicking tool pills that open modals).
      document.addEventListener('click', onCloseOptions, true)
      document.addEventListener('contextmenu', onContextMenuOptions, true)
      document.addEventListener('keydown', onEscapeOptions)
    } else {
      document.removeEventListener('click', onCloseOptions, true)
      document.removeEventListener('contextmenu', onContextMenuOptions, true)
      document.removeEventListener('keydown', onEscapeOptions)
    }

    return () => {
      document.removeEventListener('click', onCloseOptions, true)
      document.removeEventListener('contextmenu', onContextMenuOptions, true)
      document.removeEventListener('keydown', onEscapeOptions)
    }
  }, [open, onCloseOptions, onContextMenuOptions, onEscapeOptions])

  return (
    <>
      <Popover open={open}>
        <PopoverTrigger asChild>
          <div
            ref={containerRef}
            className="relative w-full flex items-center"
            onMouseDownCapture={(event) => {
              // Prevent table drag activation when interacting with selector UI.
              suppressNextFocusOpenRef.current = event.button !== 0
              if (!disabled && event.button === 0) {
                setOpen(true)
              }
              event.stopPropagation()
            }}
            onPointerDownCapture={(event) => {
              // Prevent table drag activation when interacting with selector UI.
              event.stopPropagation()
            }}
            onClick={(event) => {
              event.stopPropagation()
            }}
          >
            <Input
              ref={inputRef}
              type="text"
              placeholder="Add a tool..."
              value={searchValue}
              autoComplete="off"
              disabled={disabled}
              onChange={(event) => {
                setSearchValue(event.target.value)
              }}
              onFocus={() => {
                if (disabled) return
                if (suppressNextFocusOpenRef.current) {
                  suppressNextFocusOpenRef.current = false
                  return
                }
                setOpen(true)
              }}
              className={cn(
                'w-full p-1 border-none shadow-none rounded-lg focus:outline-none text-xs h-6 rounded-sm',
              )}
            />
            <ChevronIcon className="absolute right-2 top-1 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
        </PopoverTrigger>

        <PopoverPortal>
          <PopoverContent
            ref={popoverContentRef}
            className="bg-white z-[1000] border rounded-lg shadow-lg p-0"
            sideOffset={4}
            align="start"
            style={{
              width: containerRef.current?.offsetWidth
                ? containerRef.current.offsetWidth + 30
                : '100%',
              height: 'auto',
              maxHeight: '200px',
            }}
            onOpenAutoFocus={(event) => event.preventDefault()}
            onMouseDownCapture={(event) => {
              // Keep popover interaction local instead of bubbling to draggable table.
              event.stopPropagation()
            }}
            onPointerDownCapture={(event) => {
              // Keep popover interaction local instead of bubbling to draggable table.
              event.stopPropagation()
            }}
          >
            <div
              className="max-h-[180px] overflow-y-auto overscroll-contain text-sm"
              onWheelCapture={(event) => event.stopPropagation()}
              onTouchMoveCapture={(event) => event.stopPropagation()}
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {filteredTools.length > 0 ? (
                filteredTools.map((tool, index) => (
                  <div
                    data-testid={`tool-option--${index}`}
                    key={tool.id}
                    className={`flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-gray-100 ${
                      selectedToolId === tool.id ? 'bg-gray-200' : ''
                    }`}
                    onMouseDown={(event) => {
                      // Keep selection click local so it does not start drag.
                      event.stopPropagation()
                    }}
                    onClick={(event) => {
                      event.stopPropagation()
                      onSelect(tool)
                      setSearchValue(tool.name)
                      setOpen(false)
                      inputRef.current?.blur()
                    }}
                  >
                    {tool.name}
                    {selectedToolId === tool.id && (
                      <CheckIcon className="w-4 h-4" />
                    )}
                  </div>
                ))
              ) : (
                <p className="p-2 text-gray-500">No results found</p>
              )}
              <button
                type="button"
                className="w-full text-left p-2 mt-1 border-t border-gray-100 rounded-md hover:bg-gray-100 text-black flex items-center"
                data-testid="tool-suggestions-create-tool-button"
                onMouseDown={(event) => {
                  event.stopPropagation()
                  event.preventDefault()
                  event.nativeEvent.stopImmediatePropagation()
                }}
                onPointerDown={(event) => {
                  event.stopPropagation()
                  event.preventDefault()
                  event.nativeEvent.stopImmediatePropagation()
                }}
                onClick={(event) => {
                  event.stopPropagation()
                  event.preventDefault()
                  event.nativeEvent.stopImmediatePropagation()
                  openCreateToolModal()
                }}
              >
                <PlusCircledIcon className="w-4 h-4 mr-2" />
                <span>Create new tool</span>
              </button>
            </div>
          </PopoverContent>
        </PopoverPortal>
      </Popover>
      <CreateToolModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        initialToolName={createModalInitialToolName}
        ariaLabel="Create tool dialog"
        onToolCreated={(tool) => {
          const createdToolItem: ToolSuggestionItem = {
            id: tool.id,
            name: tool.name,
          }
          // Select newly created tool immediately so row state stays in sync.
          onSelect(createdToolItem)
          setSearchValue(tool.name)
          inputRef.current?.blur()
        }}
      />
    </>
  )
}
